import {
  isCanada,
  capitalize,
  getAPIConfig,
  getTranslateDateInformation,
} from '@tcp/core/src/utils';
import CARTITEMTILE_CONSTANTS from '../../CartItemTile/CartItemTile.constants';
import { parseStoreOpeningAndClosingTimesSFCC } from '../../../../../utils/parseStoreHours';

const getVariantVisePreOrder = (variantPreOrderDates, variantIdForPreOrder, variantSize) => {
  const preOrderDates = variantPreOrderDates?.split('|');
  let variantVisePreOrder = preOrderDates?.find((string) => {
    const variantId = string?.split('#')[0];
    return variantId === variantIdForPreOrder;
  });
  if (variantVisePreOrder) {
    variantVisePreOrder = variantVisePreOrder?.split('#');
    return {
      variant: variantIdForPreOrder,
      preOrderDate: variantVisePreOrder[1],
      size: variantSize,
    };
  }
  return {};
};

const getStoredProductDetails = (storeObj) => {
  const { language } = getAPIConfig();
  const {
    name,
    cIsStoreBossEligible,
    address1,
    address2,
    city,
    stateCode,
    postalCode,
    phone,
    bossMinDate,
    bossMaxDate,
    storeHours,
  } = storeObj;
  const { todayOpeningTime, todayClosingTime, tomorrowOpeningTime, tomorrowClosingTime } =
    parseStoreOpeningAndClosingTimesSFCC(storeHours);
  const finalData = {
    store: name ? capitalize(name) : null,
    isStoreBOSSEligible: !!cIsStoreBossEligible,
    storeAddress: address1
      ? {
          addressLine1: capitalize(address1),
          addressLine2: address2,
          city: capitalize(city),
          state: stateCode,
          zipCode: postalCode?.trim(),
        }
      : null,
    storePhoneNumber: (phone || '').trim(),
    storeTodayOpenRange: storeHours ? `${todayOpeningTime} -  ${todayClosingTime}` : null,
    storeTomorrowOpenRange: storeHours ? `${tomorrowOpeningTime} - ${tomorrowClosingTime}` : null,
    // dates for boss pickup, used getDateInformation utility
    // shows number of items from the store
    storeItemsCount: 0,
  };
  if (bossMaxDate) {
    finalData.bossStartDate = bossMinDate && getTranslateDateInformation(bossMinDate, language);
  }
  if (bossMinDate) {
    finalData.bossEndDate = bossMaxDate && getTranslateDateInformation(bossMaxDate, language);
  }
  return finalData;
};

function getInventoryConst(inventoryItem, orderItemType, isCanadaSite) {
  const {
    styleTypeUS,
    styleTypeCA,
    styleQtyUS,
    styleQtyCA,
    productIndUSStore,
    productIndCanadaStore,
    webOnlyFlagUSStore,
    webOnlyFlagCanadaStore,
    availableQty: qty,
    requestedQty,
    bossAvailableQty,
    bopisInventory,
    online,
    promoMessage,
    availableOffers,
    appliedOffers,
    isPackAndHold,
    storeSpecials,
    shippingRestrictedStates,
    c_TCPBigIdeaLicensedProductBrand: tcpBigIdeaLicensedProductBrand,
    earlyAccessAvailability,
  } = inventoryItem;
  let availableQty = qty;
  if (orderItemType.toUpperCase() === 'BOSS') {
    availableQty = bossAvailableQty;
  }
  if (orderItemType.toUpperCase() === 'BOPIS' && bopisInventory?.atcQuantity) {
    const { atcQuantity } = bopisInventory;
    availableQty = atcQuantity; // will remove qty once received atcQuantity from Inventory API. Added condtion for null to avoid undefined as availableQty
  }

  const tcpStyleType = !isCanadaSite ? styleTypeUS : styleTypeCA;
  const tcpStyleQty = !isCanadaSite ? styleQtyUS : styleQtyCA;
  const clearanceItem = !isCanadaSite
    ? productIndUSStore === 'Clearance'
    : productIndCanadaStore === 'Clearance';
  const isOnlineOnly = !isCanadaSite
    ? Boolean(parseInt(webOnlyFlagUSStore, 10))
    : Boolean(parseInt(webOnlyFlagCanadaStore, 10));
  const availability =
    availableQty - requestedQty >= 0
      ? CARTITEMTILE_CONSTANTS.AVAILABILITY.OK
      : CARTITEMTILE_CONSTANTS.AVAILABILITY.UNAVAILABLE;
  const isProductOnline = online;
  const availabilitySTH =
    qty - requestedQty >= 0
      ? CARTITEMTILE_CONSTANTS.AVAILABILITY.OK
      : CARTITEMTILE_CONSTANTS.AVAILABILITY.UNAVAILABLE;
  return {
    tcpStyleType,
    tcpStyleQty,
    clearanceItem,
    isOnlineOnly,
    availability,
    availableQty,
    availabilitySTH,
    isProductOnline,
    promoMessage,
    availableOffers,
    appliedOffers,
    isPackAndHold,
    storeSpecials,
    shippingRestrictedStates,
    tcpBigIdeaLicensedProductBrand,
    earlyAccessAvailability,
  };
}

const updateLoyaltyPayload = (loyaltyPointsRequestPayload, inventory, isCanadaSite) => {
  if (!loyaltyPointsRequestPayload) return {};
  const { loyaltyOrderItems } = loyaltyPointsRequestPayload;
  const formatLoyaltyOrderItems = loyaltyOrderItems?.map((order) => {
    const item = order;
    const itemInventory = inventory.find((itm) => itm.productId === order.productId) || {};
    const {
      loyaltyPromoMultiplierCAStore,
      loyaltyPromoMultiplierUSStore,
      loyaltyPromoPLCCMultiplierCAStore,
      loyaltyPromoPLCCMultiplierUSStore,
    } = itemInventory;

    let LoyaltyPromoMultiplierStore = loyaltyPromoMultiplierUSStore;
    let LoyaltyPromoPLCCMultiplierStore = loyaltyPromoPLCCMultiplierUSStore;
    if (isCanadaSite) {
      LoyaltyPromoMultiplierStore = loyaltyPromoMultiplierCAStore;
      LoyaltyPromoPLCCMultiplierStore = loyaltyPromoPLCCMultiplierCAStore;
    }
    if (LoyaltyPromoMultiplierStore) {
      item.LoyaltyPromoMultiplierStore = LoyaltyPromoMultiplierStore;
    }
    if (LoyaltyPromoPLCCMultiplierStore) {
      item.LoyaltyPromoPLCCMultiplierStore = LoyaltyPromoPLCCMultiplierStore;
    }

    return item;
  });

  return { ...loyaltyPointsRequestPayload, loyaltyOrderItems: formatLoyaltyOrderItems };
};

const getStoreById = (stores, orderType, storeId) => {
  return orderType === 'BOSS' || orderType === 'BOPIS'
    ? stores.find((re) => re.id === storeId)
    : null;
};

const getItemInventory = (inventory, variantNo) => {
  return inventory.find((itm) => itm.productId === variantNo) || {};
};

const getWasPrice = (wasPrice) => (wasPrice ? { wasPrice } : {});

const getColorName = (orderItem, productName, color) => {
  return orderItem?.productInfo?.isGiftCard ? productName : color;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function updateOrderDetails(orderDetails, inventory, stores) {
  const { orderItems, loyaltyPointsRequestPayload, orderAccessorieItems, orderPriceAdjustments } =
    orderDetails;
  let orderItemsParsed = [];
  const isCanadaSite = isCanada();
  if (orderItems && orderItems.length) {
    orderItemsParsed = orderItems.map((orderItem) => {
      const { productInfo, miscInfo } = orderItem;
      const { orderType, variantNo } = productInfo;
      const { storeId, orderItemType } = miscInfo;
      const itemInventory = getItemInventory(inventory, variantNo);
      const store = getStoreById(stores, orderType, storeId);
      const {
        productName,
        size,
        color,
        fit,
        listPrice: wasPrice,
        tcpPreOrderDateUSStore: preOrderDate,
        variantPreOrderDateUS,
        variantPreOrderDateCA,
        productId,
      } = itemInventory;
      const {
        tcpStyleType,
        tcpStyleQty,
        clearanceItem,
        isOnlineOnly,
        availability,
        availableQty,
        availabilitySTH,
        isProductOnline,
        promoMessage,
        availableOffers,
        appliedOffers,
        isPackAndHold,
        storeSpecials,
        shippingRestrictedStates,
        tcpBigIdeaLicensedProductBrand,
        earlyAccessAvailability,
      } = getInventoryConst(itemInventory, orderItemType, isCanadaSite);
      const newVariantPreOrderMap = isCanadaSite
        ? getVariantVisePreOrder(variantPreOrderDateCA, productId, size)
        : getVariantVisePreOrder(variantPreOrderDateUS, productId, size);
      const itemLevelPreOrderDate = orderItem?.itemInfo?.preOrderDate;
      const item = {
        productInfo: {
          ...orderItem.productInfo,
          name: productName,
          size,
          tcpStyleType,
          tcpStyleQty,
          color: {
            name: getColorName(orderItem, productName, color),
          },
          // Inventory api returns in capitalized while others are in lower case
          fit: fit?.toLowerCase(),
        },
        miscInfo: {
          ...orderItem.miscInfo,
          online: isProductOnline,
          clearanceItem,
          isOnlineOnly,
          availability,
          availableQty,
          availabilitySTH,
          promoMessage,
          availableOffers,
          appliedOffers,
          isPackAndHold,
          storeSpecials,
          shippingRestrictedStates,
          tcpBigIdeaLicensedProductBrand,
          earlyAccessAvailability,
          ...(store ? getStoredProductDetails(store) : {}),
        },
        itemInfo: {
          ...orderItem.itemInfo,
          ...getWasPrice(wasPrice),
        },
      };

      if (
        Object.keys(newVariantPreOrderMap).length &&
        itemLevelPreOrderDate !== newVariantPreOrderMap?.preOrderDate &&
        item.miscInfo.variantPreOrderMap !== newVariantPreOrderMap
      ) {
        item.miscInfo.variantPreOrderMap = newVariantPreOrderMap;
        item.itemInfo.preOrderDate = newVariantPreOrderMap?.preOrderDate;
      } else if (
        preOrderDate &&
        itemLevelPreOrderDate !== preOrderDate &&
        item.miscInfo.variantPreOrderMap?.preOrderDate !== preOrderDate
      ) {
        item.miscInfo.variantPreOrderMap = {};
        item.miscInfo.variantPreOrderMap.preOrderDate = preOrderDate;
        item.itemInfo.preOrderDate = preOrderDate;
      }
      return item;
    });
  }
  const updatedLoyaltyPayload = updateLoyaltyPayload(
    loyaltyPointsRequestPayload,
    inventory,
    isCanadaSite
  );
  return {
    updateRequired: true,
    orderItems: orderItemsParsed,
    loyaltyPointsRequestPayload: updatedLoyaltyPayload,
    orderAccessorieItems,
    orderPriceAdjustments,
  };
}

export default updateOrderDetails;
